<template>
    <div class="cancellation-reason-popover">
        <form class="kt-form kt-form--label-right" @submit.prevent="submitForm" ref="formElement">
            <div class="cancellation-reasons-list kt-radio-list">
                <label
                    v-for="cancellationReason in cancellationReasons"
                    :key="cancellationReason.value"
                    class="cancellation-reason-option kt-radio"
                    :class="{ 'kt-radio--success': cancellationReason.value === selectedReason }"
                >
                    <input
                        type="radio"
                        name="cancellationReason"
                        :value="cancellationReason.value"
                        v-model="selectedReason"
                    />
                    <span></span>
                    {{ translations.enums.cancellationReason[cancellationReason.name] }}
                </label>
            </div>
        </form>

        <VButton @click="submitForm" :disabled="isSubmitDisabled" class="cancel-order-confirmation-btn primary rounded">
            {{ translations.general.confirm }}
        </VButton>
    </div>
</template>

<script setup lang="ts">
    import { ButtonType, Popup } from "@/components/dialog/Popup";
    import type { IAddCancellationCmd } from "@/data/commands/order/AddCancellationCmd";
    import { Factory } from "@/data/dao/Factory";
    import { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import { CancellationMode, CancellationReason } from "@/data/model/Enums";
    import { exceptionTypes } from "@/data/util/Config";
    import { Misc } from "@/data/util/Misc";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onMounted, ref } from "vue";
    import VButton from "@/components/elements/VButton.vue";

    const emit = defineEmits(["goBack", "saveStart", "saveError", "saveEnd"]);

    const props = defineProps<{
        locationId: string;
        orderId: string;
        cancellableAmount: number;
        data: IAddCancellationCmd;
        onSaveStart?: () => void;
        onSaveError?: (errorCode: string, errorMessage: string) => void;
        onSaveEnd?: () => void;
    }>();

    const dao = Factory.getInstance<OrderDao>(OrderDao);

    // define html refs
    const formElement = ref<HTMLFormElement | null>(null);
    const isSaving = ref(false);
    const isSubmitDisabled = computed(() => !isInitialized.value || isSaving.value || selectedReason.value === null);
    const isInitialized = ref(false);
    const hasAvailableItemsWithStockManagement = ref(false);
    const shouldUpdateStock = ref<boolean | undefined>(undefined);

    // Define the reasons object from the enum
    const cancellationReasons = ref(Misc.enumsToValueNameArray(CancellationReason));

    // Variable to track the selected reason
    const selectedReason = ref<CancellationReason | null>(null);

    async function submitForm() {
        if (isStockChangeApprovalNeeded(selectedReason.value!)) {
            showConfirmStockChangePopover();
        } else {
            showCancellationConfirmPopover();
        }
    }

    function isStockChangeApprovalNeeded(reason: CancellationReason) {
        const reasonsWithStockChangeApproval = [
            CancellationReason.IncorrectIncompleteItemInformation,
            CancellationReason.OrderedIncorrectlyEndUser,
            CancellationReason.OrderedIncorrectlyStaffMember,
            CancellationReason.EndUserChangedMind,
            CancellationReason.PriceWrong,
            CancellationReason.LateFulfilment,
            CancellationReason.Other,
        ];

        return hasAvailableItemsWithStockManagement.value && reasonsWithStockChangeApproval.includes(reason);
    }

    function showConfirmStockChangePopover() {
        Popup.show({
            title: "",
            message: translations.cancellation.canStockBeReused,
            confirmHandler: () => {
                shouldUpdateStock.value = true;
                showCancellationConfirmPopover();
            },
            confirmButton: translations.general.yes,
            cancelHandler: () => {
                shouldUpdateStock.value = false;
                showCancellationConfirmPopover();
            },
            cancelButton: translations.general.no,
            uniqueId: "btn btn-danger",
        });
    }

    function showCancellationConfirmPopover() {
        Popup.show({
            message: translations.cancellation.confirmMessage.replace("{{amt}}", props.cancellableAmount.toFixed(2)),
            title: translations.cancellation.confirmTitle,
            confirmHandler: () => {
                addCancellation();
            },
            confirmButton: translations.cancellation.submitBtn,
            cancelButton: translations.cancellation.notNow,
            highlightConfirm: true,
            containerClass: "cancellation-confirm-popup-container",
            confirmButtonType: ButtonType.danger,
        });
    }

    async function addCancellation() {
        emit("saveStart");
        if (props.onSaveStart) {
            props.onSaveStart();
        }
        isSaving.value = true;

        try {
            const addCancellationCommand = {
                ...props.data,
                reason: selectedReason.value!,
                updateStock: shouldUpdateStock.value,
            };
            await dao.addCancellation(props.orderId, addCancellationCommand);

            if (props.onSaveEnd) {
                props.onSaveEnd();
            }
        } catch (ex) {
            logError(ex);

            isSaving.value = false;

            let errorMessage = translations.cancellation.cancellationFailed;
            let errorCode = "unknown";
            if (ex instanceof DaoError) {
                errorCode = ex.type;
                if (ex.type === exceptionTypes.cancellationNotAllowedForPayment) {
                    errorMessage = translations.cancellation.cancellationNotAllowedForPayment;
                } else if (ex.type === exceptionTypes.singleCancellationFailedException) {
                    errorMessage = translations.cancellation.singleCancellationFailed;
                } else if (ex.type === exceptionTypes.multipleCancellationsFailedException) {
                    errorMessage = translations.cancellation.multipleCancellationsFailed;
                } else if (ex.type === exceptionTypes.refundOutcomeUnknownException) {
                    errorMessage = translations.cancellation.refundOutcomeUnknown;
                } else if (ex.type === exceptionTypes.multiplePdPaymentCancellationsNotSupportedException) {
                    errorMessage = translations.cancellation.multiplePdPaymentCancellationsNotSupported;
                } else if (ex.type === exceptionTypes.twintOnlineRefundNotSupportedException) {
                    errorMessage = translations.cancellation.twintOnlineRefundNotSupported;
                }
            }

            if (props.onSaveError) {
                props.onSaveError(errorCode, errorMessage);
            }
        } finally {
            close();
        }
    }

    async function loadStockManagementStatus() {
        if (props.data.mode == CancellationMode.Amount) {
            hasAvailableItemsWithStockManagement.value = false;
            isInitialized.value = true;
            return;
        }

        try {
            const status = await dao.getOrderStockManagementStatus(
                props.orderId,
                props.data.mode,
                props.data.positions
            );
            hasAvailableItemsWithStockManagement.value = status.hasAvailableItemsWithStockManagement;
            isInitialized.value = true;
        } catch (ex) {
            logError(ex);
            Popup.show({ message: translations.general.failedLoadingData });
            close();
        }
    }

    onMounted(async () => {
        await loadStockManagementStatus();
    });
</script>

<style lang="scss">
    .cancellation-reason-popover {
        padding-bottom: 14px;

        .cancel-order-confirmation-btn.v-btn.primary {
            font-size: 17px;
            margin-top: 0.625em;
            height: 2.75em;
            line-height: 2.75em;
            padding: 0 2em;
            border-radius: 1.5em !important;
            width: 100%;

            &.hidden {
                display: none;
            }
        }

        .cancellation-reasons-list {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 0 1.5em;

            .cancellation-reason-option {
                user-select: none;
                padding-top: 5px;
                padding-bottom: 5px;
                margin: 0;

                > span {
                    top: 6px;
                }
            }
        }

        .btn[disabled] {
            cursor: default;
        }
    }
</style>
